import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { canEnterApp } from '@helios/authz';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qPatientCommunicationPreferences,
  qPatientById,
  qPatientContact,
  type PatientCommunicationPreferencesClientRow,
  type PatientDetailRow,
  type PatientContactRow,
} from '@helios/data-access';
import { usePowerSyncQuery, usePowerSyncDb } from '../adapters/powersync';
import {
  setWhatsAppOptIn,
  whatsAppOptInState,
  OptOutReasonRequiredError,
  type WhatsAppOptInState,
} from '../lib/patient-communication-preferences';

/* ------------------------------------------------------------------ */
/*  WhatsAppOptInControl (PRD.222)                                    */
/*                                                                    */
/*  Patient Contact-section control for WhatsApp messaging consent.   */
/*                                                                    */
/*  - Displays current opt-in state (read-only for everyone).         */
/*  - Authorized roles (admin/editor) get enable/disable actions.     */
/*  - Disable requires a reason and stamps whatsapp_opt_out_at.        */
/*  - Writes go through PowerSync → syncWrite (never direct SQL).     */
/*  - A missing prefs row reads as "not opted in".                    */
/* ------------------------------------------------------------------ */

/** Roles allowed to change a patient's communication consent. */
const ALLOWED_ROLES = ['admin', 'editor'] as const;

export interface WhatsAppOptInControlProps {
  patientId: string;
}

export function WhatsAppOptInControl({ patientId }: WhatsAppOptInControlProps) {
  const { claims } = useAuth();
  const { db } = usePowerSyncDb();
  const orgId = claims?.activeOrg ?? '';

  const { data: prefRows } = usePowerSyncQuery<PatientCommunicationPreferencesClientRow>(
    qPatientCommunicationPreferences(patientId),
  );
  const prefsRow = prefRows[0] ?? null;
  const state: WhatsAppOptInState = whatsAppOptInState(prefsRow);

  // Patient context for the confirm dialogs.
  const { data: patientRows } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  const { data: contactRows } = usePowerSyncQuery<PatientContactRow>(qPatientContact(patientId));
  const patientName = patientRows[0]?.full_name ?? 'This patient';
  const whatsAppNumber = formatNumber(
    contactRows[0]?.mobile_country_code ?? null,
    contactRows[0]?.mobile_number ?? null,
  );

  const canManage = canEnterApp(claims, {
    appId: 'whatsapp-opt-in-management',
    entryAnyOfRoles: [...ALLOWED_ROLES],
  });

  const [enableOpen, setEnableOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  const openEnable = useCallback(() => {
    setError(null);
    setEnableOpen(true);
  }, []);

  const openDisable = useCallback(() => {
    setError(null);
    setReason('');
    setDisableOpen(true);
  }, []);

  const handleEnable = useCallback(async () => {
    if (busy || !db || !orgId) return;
    setBusy(true);
    setError(null);
    try {
      await setWhatsAppOptIn({ db, orgId, patientId, existingRow: prefsRow, optIn: true });
      setEnableOpen(false);
      showToast('Patient opted in to WhatsApp messaging.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update opt-in.');
    } finally {
      setBusy(false);
    }
  }, [busy, db, orgId, patientId, prefsRow, showToast]);

  const handleDisable = useCallback(async () => {
    if (busy || !db || !orgId) return;
    setBusy(true);
    setError(null);
    try {
      const wrote = await setWhatsAppOptIn({
        db,
        orgId,
        patientId,
        existingRow: prefsRow,
        optIn: false,
        optOutReason: reason,
      });
      setDisableOpen(false);
      showToast(
        wrote ? 'Patient opted out of WhatsApp messaging.' : 'Patient was already not opted in.',
      );
    } catch (err) {
      if (err instanceof OptOutReasonRequiredError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update opt-in.');
      }
    } finally {
      setBusy(false);
    }
  }, [busy, db, orgId, patientId, prefsRow, reason, showToast]);

  const optedOutAt =
    state === 'opted-out' && prefsRow?.whatsapp_opt_out_at
      ? new Date(prefsRow.whatsapp_opt_out_at).toLocaleDateString()
      : null;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
          paddingTop: 'var(--sp-3)',
          borderTop: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-placeholder)',
            }}
          >
            WhatsApp Reminders
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 'var(--line-height-base)',
              color: STATE_COLOR[state],
            }}
          >
            {STATE_LABEL[state]}
            {optedOutAt ? ` · ${optedOutAt}` : ''}
          </span>
          {state === 'opted-out' && prefsRow?.whatsapp_opt_out_reason && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Reason: {prefsRow.whatsapp_opt_out_reason}
            </span>
          )}
        </div>

        {canManage &&
          (state === 'opted-in' ? (
            <Button variant="outline" size="sm" onClick={openDisable} disabled={busy}>
              Disable
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={openEnable} disabled={busy}>
              Enable
            </Button>
          ))}
      </div>

      {/* Enable confirm */}
      <Modal
        open={enableOpen}
        onOpenChange={setEnableOpen}
        title="Enable WhatsApp Reminders"
        description="Confirm that this patient consents to receive WhatsApp messages."
        maxWidth={440}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button variant="ghost" size="md" onClick={() => setEnableOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="gradient" size="md" onClick={handleEnable} disabled={busy}>
              {busy ? 'Saving…' : 'Enable Reminders'}
            </Button>
          </div>
        }
      >
        <div style={modalBodyStyle}>
          <SummaryBox patientName={patientName} whatsAppNumber={whatsAppNumber} />
          <p style={noteTextStyle}>
            This records the patient&apos;s consent in PowerCareAI so staff can send WhatsApp
            appointment reminders. It&apos;s a consent record only — separate from the messaging
            provider&apos;s own setup (e.g. a Twilio sandbox join) — and does not by itself
            guarantee delivery.
          </p>
          {error && <div style={errorBoxStyle}>{error}</div>}
        </div>
      </Modal>

      {/* Disable confirm — reason required */}
      <Modal
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title="Disable WhatsApp Reminders"
        description="Record that this patient no longer consents to WhatsApp messages."
        maxWidth={440}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button variant="ghost" size="md" onClick={() => setDisableOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              size="md"
              onClick={handleDisable}
              disabled={busy || reason.trim().length === 0}
            >
              {busy ? 'Saving…' : 'Disable'}
            </Button>
          </div>
        }
      >
        <div style={modalBodyStyle}>
          <SummaryBox patientName={patientName} whatsAppNumber={whatsAppNumber} />
          <p style={noteTextStyle}>
            Staff will no longer be able to send WhatsApp reminders to this patient. A reason is
            required and is stored with the opt-out.
          </p>
          <div>
            <label htmlFor="wa-opt-out-reason" style={fieldLabelStyle}>
              Reason for opting out
            </label>
            <textarea
              id="wa-opt-out-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. patient requested to stop WhatsApp messages"
              style={{
                width: '100%',
                padding: 'var(--sp-3) var(--input-padding-x)',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-base)',
                color: 'var(--input-text)',
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {error && <div style={errorBoxStyle}>{error}</div>}
        </div>
      </Modal>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 'var(--sp-6)',
            right: 'var(--sp-6)',
            zIndex: 1000,
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: '#dcfce7',
            color: '#15803d',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Confirm-dialog summary block                                      */
/* ------------------------------------------------------------------ */

function SummaryBox({
  patientName,
  whatsAppNumber,
}: {
  patientName: string;
  whatsAppNumber: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <SummaryRow label="Patient" value={patientName} />
      <SummaryRow label="WhatsApp" value={whatsAppNumber} />
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** Format a country code + number into a display string (e.g. "+63 9778310963"). */
function formatNumber(countryCode: string | null, number: string | null): string {
  if (!number) return 'No number on file';
  const cc = countryCode?.trim().replace(/^\+/, '');
  return cc ? `+${cc} ${number}` : number;
}

/* ------------------------------------------------------------------ */
/*  Static maps + styles                                              */
/* ------------------------------------------------------------------ */

const STATE_LABEL: Record<WhatsAppOptInState, string> = {
  'opted-in': 'Opted in',
  'opted-out': 'Opted out',
  'not-set': 'Not opted in',
};

const STATE_COLOR: Record<WhatsAppOptInState, string> = {
  'opted-in': '#15803d',
  'opted-out': '#b45309',
  'not-set': 'var(--text-muted)',
};

/**
 * Modal body padding — the shared Modal component pads only its header and
 * footer, so each caller must pad its own body content.
 */
const modalBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4)',
  padding: 'var(--sp-5, 20px) var(--sp-6, 24px)',
};

const noteTextStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text-muted)',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--text-muted)',
  marginBottom: 'var(--sp-2)',
};

const errorBoxStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  background: '#fef2f2',
  color: '#dc2626',
  fontSize: 'var(--font-size-sm)',
};
